/**
 * Hook for handling the migration from sessionless chat to a persisted chat.
 *
 * When a user sends a message in sessionless mode, the chat is created server-side
 * and we receive a chatId. This hook handles:
 * 1. Moving state from sessionless key to the chat-specific key
 * 2. Starting the stream with the persisted chatId
 * 3. Cleaning up temporary file/attachment atoms
 * 4. Tracking analytics events
 */

import {
  linkedPatientContextAtom,
  linkedSessionContextAtom,
  sessionlessAttachmentIdsAtom,
  sessionlessFilesAtom,
} from '@/atoms/chatAtoms';
import type { SessionConversation, UserMessageEntry } from '@heidi/evidence/contracts';
import type { ChatState } from '@/components/chat/types/types';
import { ChatMode } from '@heidi/evidence/contracts';
import { appendCheckpoint, TrackingKey } from '@heidi/runtime/performance';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { TIMELINE_STREAM_ERROR } from '@/utils/chatErrors';
import { logger } from '@/utils/logger/logger';
import { getDefaultStore, useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { track } from '@/lib/analytics/track';

const SESSIONLESS_KEY = 'sessionless';

interface UseSessionlessChatMigrationParams {
  /** Current chat ID (null/undefined if new chat) */
  chatId: string | null | undefined;
  /** Session ID (null if sessionless) */
  sessionId: string | null | undefined;
  /** State key for the current chat */
  stateKey: string;
  /** Whether the chat is sessionless */
  isSessionless: boolean;
  /** Effective chat mode */
  effectiveMode: ChatMode;
  /** Effective domains for evidence search */
  effectiveDomains: string[] | undefined;
  /** Current sessionless state slice (from chatState) */
  sessionlessState: SessionConversation | undefined;
  /**
   * EVI-1680: the `session-${sessionId}-new` state slice. Used to resume the
   * deferred stream for an eager-created in-session chat (the SBS first-message
   * path). Only acted on when its metadata carries `sbsPendingResume`, so normal
   * in-session chats are untouched.
   */
  sessionNewState?: SessionConversation | undefined;
  /** Key for the `session-${sessionId}-new` slice (null when sessionless). */
  sessionNewKey?: string | null;
  /** Current chat state's streaming state */
  currentChatStreamingState: SessionConversation['streamingState'] | undefined;
  /** Function to update chat state */
  setChatState: (fn: (prev: ChatState) => ChatState) => void;
  /** Function to start streaming */
  startStreaming: (
    timeline: UserMessageEntry[],
    overrideMode?: ChatMode,
    onError?: (err: string) => void,
    chatId?: string | null,
    files?: File[],
    attachmentIds?: string[]
  ) => Promise<void>;
  /** Function to set error state */
  setError: (err: string | null) => void;
  /** Ref to track query length for analytics */
  lastQueryLengthRef: React.MutableRefObject<number>;
}

/**
 * Handles the migration from sessionless state to a persisted chat.
 *
 * This effect runs when:
 * - We're in sessionless mode
 * - A chatId becomes available (chat was created)
 * - The chatId matches the clientChatId stored in sessionless state
 *
 * It moves the user message from sessionless state to the chat-specific state,
 * starts the stream, and tracks analytics.
 */
export function useSessionlessChatMigration({
  chatId,
  sessionId,
  stateKey,
  isSessionless,
  effectiveMode,
  effectiveDomains,
  sessionlessState,
  sessionNewState,
  sessionNewKey,
  currentChatStreamingState,
  setChatState,
  startStreaming,
  setError,
  lastQueryLengthRef,
}: UseSessionlessChatMigrationParams): void {
  const { trackChatSubmitted } = useEvidenceAnalytics();
  const linkedSession = useAtomValue(linkedSessionContextAtom);
  const linkedPatient = useAtomValue(linkedPatientContextAtom);

  const processedChatIdRef = useRef<string | null>(null);
  const store = getDefaultStore();

  useEffect(() => {
    // Sessionless: the pending message lives under SESSIONLESS_KEY.
    // In-session (EVI-1680): the pending message lives under
    // `session-${sessionId}-new`, but we only resume it for the eager-created
    // SBS first-message case, flagged by `sbsPendingResume`. This leaves the
    // normal in-session lazy-creation flow completely untouched.
    const isInSessionResume =
      !isSessionless && !!sessionNewState?.metadata?.sbsPendingResume;

    if (!isSessionless && !isInSessionResume) {
      return;
    }

    if (processedChatIdRef.current === chatId) {
      return;
    }

    const pendingState = isSessionless ? sessionlessState : sessionNewState;
    const pendingKey = isSessionless ? SESSIONLESS_KEY : sessionNewKey;
    if (!pendingKey) {
      return;
    }

    // Use the already-extracted pending state to avoid a chatState dependency
    const clientChatId = pendingState?.metadata?.clientChatId;
    const pendingMode = pendingState?.metadata?.pendingMode;
    // Reuse the user message from the pending timeline instead of creating a new one
    const firstEntry = pendingState?.timeline?.[0];
    const userMessage = firstEntry?.type === 'user_message' ? firstEntry : undefined;

    // Retrieve File objects for this message (from atom)
    const pendingFiles = userMessage
      ? (store.get(sessionlessFilesAtom(userMessage.id)) ?? undefined)
      : undefined;

    // Retrieve pre-uploaded attachment IDs for this message (from atom)
    const pendingAttachmentIds = userMessage
      ? (store.get(sessionlessAttachmentIdsAtom(userMessage.id)) ?? undefined)
      : undefined;

    // Only process if chatId matches the client-generated ID to prevent race conditions
    // NOTE: We check currentChatStreamingState (not conversation.streamingState)
    // because conversation may return sessionlessState which has streamingState from the initial send.
    // We only want to block if we're already streaming for THIS chat key.
    if (
      chatId &&
      clientChatId &&
      chatId === clientChatId &&
      userMessage &&
      !currentChatStreamingState
    ) {
      processedChatIdRef.current = chatId;

      // Move state from the pending key (SESSIONLESS_KEY or
      // `session-${sessionId}-new`) to the chat key in a single update.
      setChatState((prev) => {
        const rest = { ...prev };
        delete rest[pendingKey];
        return {
          ...rest,
          [stateKey]: {
            timeline: [userMessage],
            streamingState: null,
            metadata: { lastActivity: Date.now(), clientChatId: chatId },
          },
        };
      });

      void startStreaming(
        [userMessage],
        pendingMode,
        (err) => {
          setError(err);
        },
        chatId,
        pendingFiles,
        pendingAttachmentIds
      ).catch((error: unknown) => {
        logger.error('Failed to resume migrated chat stream', {
          error,
          chatId,
          sessionId: sessionId ?? null,
        });
        setError(TIMELINE_STREAM_ERROR.QUERY_FAILED);
      });

      // Clean up files and attachment IDs from atoms after use
      if (userMessage) {
        const filesAtom = sessionlessFilesAtom(userMessage.id);
        store.set(filesAtom, null);

        const attachmentIdsAtom = sessionlessAttachmentIdsAtom(userMessage.id);
        store.set(attachmentIdsAtom, null);
      }

      const modeToTrack = pendingMode ?? effectiveMode ?? 'evidence';
      // Track Evidence chat submitted
      lastQueryLengthRef.current = userMessage.content.length;
      trackChatSubmitted({
        query: userMessage.content,
        sources: effectiveDomains,
        chatId,
        sessionId: sessionId ?? undefined,
      });
      appendCheckpoint({
        key: TrackingKey.EVIDENCE_MESSAGE_SEND,
        checkpointName: 'message_submitted',
        metadata: {
          chat_id: chatId,
          session_id: sessionId ?? null,
        },
      });

      // Track chat message sent with context (only if has context)
      const hasAttachments =
        (pendingAttachmentIds && pendingAttachmentIds.length > 0) ||
        (pendingFiles && pendingFiles.length > 0);
      const hasLinkedSession = !!linkedSession?.id;
      const hasLinkedPatient = !!linkedPatient?.id;
      if (hasAttachments || hasLinkedSession || hasLinkedPatient) {
        const attachmentTypes = userMessage.files?.map((f) => f.type).filter(Boolean);

        track({ options: { amplitude: false },
          event_name: TrackingEventName.ChatMessageSentWithContext,
          props: {
            has_linked_session: hasLinkedSession,
            has_linked_patient: hasLinkedPatient,
            has_attachments: !!hasAttachments,
            attachment_count: pendingAttachmentIds?.length ?? pendingFiles?.length ?? 0,
            attachment_types:
              attachmentTypes && attachmentTypes.length > 0 ? attachmentTypes : undefined,
            linked_session_id: linkedSession?.id,
            linked_patient_id: linkedPatient?.id,
            chat_id: chatId,
            session_id: sessionId ?? undefined,
            message_length: userMessage.content.length,
            mode: modeToTrack,
          },
        });
      }
    }
  }, [
    chatId,
    sessionlessState,
    sessionNewState,
    sessionNewKey,
    currentChatStreamingState,
    stateKey,
    setChatState,
    startStreaming,
    effectiveMode,
    isSessionless,
    effectiveDomains,
    sessionId,
    trackChatSubmitted,
    store,
    linkedSession?.id,
    linkedPatient?.id,
    setError,
    lastQueryLengthRef,
  ]);
}
